import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Grid,
  Divider,
  LinearProgress,
  InputAdornment,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PhotoCamera,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';

// Configuration - Replace these with your actual values
const API_BASE_URL = import.meta.env.VITE_API_URL || "https://socialmedia.gtlit.com";
const TWITTER_TOKEN = localStorage.getItem("twitter_token");
const TOKEN = localStorage.getItem("token");

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const Profile = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedProfileImage, setSelectedProfileImage] = useState(null); // for preview

  const [profileImageFile, setProfileImageFile] = useState(null); // for API
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState({
    basic: false,
    company: false,
    password: false,
  });

  // Profile Data States
  const [profileData, setProfileData] = useState({

    profileUrl: '',
    role: '',
    companyName: '',
    profile: '',
    mobile: '',
    panNumber: '',
    ownerId: '',
    gstNumber: '',
    screenshotUrl: '',
    gstState: '',
    activePlanId: '',
    companyAddress: '',
    name: '',
    id: '',
    userType: '',
    email: '',
    username: '',
    status: ''
  });

  const [originalData, setOriginalData] = useState({});

  // Password States
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [passwordStrength, setPasswordStrength] = useState({
    strength: 0,
    text: '',
  });

  // Alert States
  const [alert, setAlert] = useState({
    show: false,
    type: 'success',
    message: '',
  });

  // Fetch Profile Data on Mount
  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/get_profile_details?token=${TWITTER_TOKEN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${TOKEN}`,
        },
        body: JSON.stringify({
          token: TWITTER_TOKEN,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch profile data');
      }

      const result = await response.json();
      console.log(result);
      setProfileData(result.data);
      setOriginalData(result.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      showAlert('error', 'Failed to load profile data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => {
      setAlert({ show: false, type: 'success', message: '' });
    }, 5000);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const toggleEditMode = (section) => {
    setEditMode((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));

    // Reset to original data if canceling
    if (editMode[section]) {
      if (section === 'password') {
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: '',
        });
        setPasswordStrength({ strength: 0, text: '' });
      } else {
        setProfileData(originalData);
      }
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === 'new_password') {
      checkPasswordStrength(value);
    }
  };

  const checkPasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength({ strength: 0, text: '' });
      return;
    }

    let strength = 0;

    if (password.length >= 8) strength += 20;
    if (password.length >= 12) strength += 20;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 20;
    if (/\d/.test(password)) strength += 20;
    if (/[^a-zA-Z\d]/.test(password)) strength += 20;

    let text = 'Weak';
    if (strength > 60) text = 'Medium';
    if (strength >= 80) text = 'Strong';

    setPasswordStrength({ strength, text });
  };

    // Modify image input handler
  const handleProfileImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // For preview
      const reader = new FileReader();
      reader.onload = (e) => setSelectedProfileImage(e.target.result);
      reader.readAsDataURL(file);
      setProfileImageFile(file);
    }
  };

  const handleImageUpload = async () => {
    if (!profileImageFile) return;
  
    try {
      const formData = new FormData();
      formData.append('token', TWITTER_TOKEN);
      formData.append('file', profileImageFile);
    
      const response = await fetch(`${API_BASE_URL}/profile_image_upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${TOKEN}`,
        },
        body: formData,
      });
    
      if (!response.ok) throw new Error('Failed to upload image');
    
      setProfileData((prev) => ({
        ...prev,
        profile_image: selectedProfileImage,
      }));
      setProfileImageFile(null);
      setSelectedProfileImage(null);
    
      showAlert('success', 'Profile image updated successfully!');
    } catch (error) {
      console.error('Error uploading image:', error);
      showAlert('error', 'Failed to upload image. Please try again.');
    }
  };

  const handleBasicSubmit = async () => {
    try {
      const payload = {
        token: TWITTER_TOKEN,
        name: profileData.name,
        username: profileData.username,
        email: profileData.email,
        mobile: profileData.mobile,
        // Keep company data unchanged
        companyName: profileData.companyName,
        companyAddress: profileData.companyAddress,
        gstNumber: profileData.gstNumber,
        gstState: profileData.gstState,
        panNumber: profileData.panNumber,
      };

      const response = await fetch(`${API_BASE_URL}/update_profile_details?token=${TWITTER_TOKEN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${TOKEN}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to update basic information');
      }

      setOriginalData((prev) => ({
        ...prev,
        name: profileData.name,
        username: profileData.username,
        email: profileData.email,
        mobile: profileData.mobile,
      }));

      showAlert('success', 'Basic information updated successfully!');
      toggleEditMode('basic');
    } catch (error) {
      console.error('Error updating basic info:', error);
      showAlert('error', 'Failed to update basic information. Please try again.');
    }
  };

  const handleCompanySubmit = async () => {
    try {
      const payload = {
        token: TWITTER_TOKEN,
        // Keep basic data unchanged
        name: originalData.name,
        username: originalData.username,
        email: originalData.email,
        mobile: originalData.mobile,
        // Update company data
        companyName: profileData.companyName,
        companyAddress: profileData.companyAddress,
        gstNumber: profileData.gstNumber,
        gstState: profileData.gstState,
        panNumber: profileData.panNumber,
      };
      const response = await fetch(`${API_BASE_URL}/update_profile_details?token=${TWITTER_TOKEN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${TOKEN}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to update company information');
      }

      setOriginalData((prev) => ({
        ...prev,
        companyName: profileData.companyName,
        companyAddress: profileData.companyAddress,
        gstNumber: profileData.gstNumber,
        gstState: profileData.gstState,
        panNumber: profileData.panNumber,
      }));

      showAlert('success', 'Company information updated successfully!');
      toggleEditMode('company');
    } catch (error) {
      console.error('Error updating company info:', error);
      showAlert('error', 'Failed to update company information. Please try again.');
    }
  };

  const handlePasswordSubmit = async () => {
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      showAlert('error', 'Please fill in all password fields.');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      showAlert('error', 'New password and confirmation do not match.');
      return;
    }

    if (passwordData.new_password.length < 8) {
      showAlert('error', 'New password must be at least 8 characters long.');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/change_password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${TOKEN}`,
        },
        body: JSON.stringify({
          token: TWITTER_TOKEN,
          previous_password: passwordData.current_password,
          new_password: passwordData.new_password,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to change password');
      }

      showAlert('success', 'Password changed successfully!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
      setPasswordStrength({ strength: 0, text: '' });
      toggleEditMode('password');
    } catch (error) {
      console.error('Error changing password:', error);
      showAlert('error', 'Failed to change password. Please check your current password and try again.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box textAlign="center" mb={4}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          My Profile
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your account information and settings
        </Typography>
      </Box>

      {/* Alert */}
      {alert.show && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert({ ...alert, show: false })}>
          {alert.message}
        </Alert>
      )}

      {/* Profile Image */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, textAlign: 'center' }}>
        <Box position="relative" display="inline-block">
          <Avatar
            src={
              selectedProfileImage
                || profileData.profileUrl
                || 'https://via.placeholder.com/120'
            }
            sx={{ width: 120, height: 120, margin: '0 auto', mb: 2 }}
          />
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="profile-image-upload"
            type="file"
            onChange={handleProfileImageSelect}
          />
          <label htmlFor="profile-image-upload">
            <IconButton
              component="span"
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 0,
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              <PhotoCamera />
            </IconButton>
          </label>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Click the camera icon to change your profile photo
        </Typography>
        {selectedProfileImage && (
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={handleImageUpload}
          >
            Update Profile
          </Button>
        )}
      </Paper>

      {/* Tabs */}
      <Paper elevation={2}>
        <Tabs value={tabValue} onChange={handleTabChange} centered>
          <Tab label="Basic" />
          <Tab label="Company" />
          <Tab label="Password" />
        </Tabs>

        {/* Basic Information Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box px={3} pb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6" fontWeight={600}>
                Basic Information
              </Typography>
              <Button
                startIcon={editMode.basic ? <CancelIcon /> : <EditIcon />}
                onClick={() => toggleEditMode('basic')}
                variant={editMode.basic ? 'outlined' : 'contained'}
              >
                {editMode.basic ? 'Cancel' : 'Edit'}
              </Button>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={profileData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  disabled={!editMode.basic}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Username"
                  value={profileData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  disabled={!editMode.basic}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={!editMode.basic}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={profileData.mobile}
                  onChange={(e) => handleInputChange('mobile', e.target.value)}
                  disabled={!editMode.basic}
                />
              </Grid>
            </Grid>

            {editMode.basic && (
              <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
                <Button variant="outlined" onClick={() => toggleEditMode('basic')}>
                  Cancel
                </Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleBasicSubmit}>
                  Save Changes
                </Button>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Company Information Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box px={3} pb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6" fontWeight={600}>
                Company Information
              </Typography>
              <Button
                startIcon={editMode.company ? <CancelIcon /> : <EditIcon />}
                onClick={() => toggleEditMode('company')}
                variant={editMode.company ? 'outlined' : 'contained'}
              >
                {editMode.company ? 'Cancel' : 'Edit'}
              </Button>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={profileData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  disabled={!editMode.company}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Address"
                  value={profileData.companyAddress}
                  onChange={(e) => handleInputChange('companyAddress', e.target.value)}
                  disabled={!editMode.company}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="GST Number"
                  value={profileData.gstNumber}
                  onChange={(e) => handleInputChange('gstNumber', e.target.value)}
                  disabled={!editMode.company}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="GST State"
                  value={profileData.gstState}
                  onChange={(e) => handleInputChange('gstState', e.target.value)}
                  disabled={!editMode.company}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="PAN Number"
                  value={profileData.panNumber}
                  onChange={(e) => handleInputChange('panNumber', e.target.value)}
                  disabled={!editMode.company}
                />
              </Grid>
            </Grid>

            {editMode.company && (
              <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
                <Button variant="outlined" onClick={() => toggleEditMode('company')}>
                  Cancel
                </Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleCompanySubmit}>
                  Save Changes
                </Button>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Password Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box px={3} pb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6" fontWeight={600}>
                Change Password
              </Typography>
              <Button
                startIcon={editMode.password ? <CancelIcon /> : <EditIcon />}
                onClick={() => toggleEditMode('password')}
                variant={editMode.password ? 'outlined' : 'contained'}
              >
                {editMode.password ? 'Cancel' : 'Edit'}
              </Button>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Current Password"
                  type={showPassword.current ? 'text' : 'password'}
                  value={passwordData.current_password}
                  onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                  disabled={!editMode.password}
                  InputProps={{
                    endAdornment: editMode.password && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowPassword((prev) => ({ ...prev, current: !prev.current }))
                          }
                          edge="end"
                        >
                          {showPassword.current ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword.new ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={(e) => handlePasswordChange('new_password', e.target.value)}
                  disabled={!editMode.password}
                  InputProps={{
                    endAdornment: editMode.password && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => ({ ...prev, new: !prev.new }))}
                          edge="end"
                        >
                          {showPassword.new ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {editMode.password && passwordData.new_password && (
                  <Box mt={1}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        Password Strength
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {passwordStrength.text}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={passwordStrength.strength}
                      color={
                        passwordStrength.strength < 40
                          ? 'error'
                          : passwordStrength.strength < 70
                          ? 'warning'
                          : 'success'
                      }
                    />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showPassword.confirm ? 'text' : 'password'}
                  value={passwordData.confirm_password}
                  onChange={(e) => handlePasswordChange('confirm_password', e.target.value)}
                  disabled={!editMode.password}
                  InputProps={{
                    endAdornment: editMode.password && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowPassword((prev) => ({ ...prev, confirm: !prev.confirm }))
                          }
                          edge="end"
                        >
                          {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            {editMode.password && (
              <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
                <Button variant="outlined" onClick={() => toggleEditMode('password')}>
                  Cancel
                </Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={handlePasswordSubmit}>
                  Update Password
                </Button>
              </Box>
            )}
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default Profile;